import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { appointmentService } from '../services/AppointmentService';
import { clientService } from '../services/ClientService';
import { userService } from '../services/UserService';
import { Appointment, AppointmentFormData } from '../models/Appointment';
import { Client } from '../models/Client';
import { User } from '../models/User';
import { Calendar, Plus, Clock, MapPin, User as UserIcon, Video, Filter, List, Grid } from 'lucide-react';
import { AddAppointmentModal } from '../components/modals/AddAppointmentModal';
import { format, isToday, isTomorrow, startOfDay } from 'date-fns';

export const Appointments: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { company } = useCompany();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddAppointmentModal, setShowAddAppointmentModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterRbt, setFilterRbt] = useState<string>('');
  const [filterClient, setFilterClient] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterStartDate, setFilterStartDate] = useState<string>('');
  const [filterEndDate, setFilterEndDate] = useState<string>('');

  // View mode (list or table)
  const [viewMode, setViewMode] = useState<'list' | 'table'>('list');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, currentUser]);

  const loadData = async () => {
    if (!company || !currentUser) {
      setLoading(false);
      return;
    }

    try {
      // Load clients and users for display
      const [companyClients, companyUsers] = await Promise.all([
        clientService.getClientsByCompany(company.id),
        userService.getUsersByCompany(company.id),
      ]);
      setClients(companyClients);
      setUsers(companyUsers);

      // Load appointments based on role
      let userAppointments: Appointment[] = [];
      if (currentUser.role === 'rbt') {
        // RBT sees their own appointments
        userAppointments = await appointmentService.getAppointmentsByRbt(currentUser.id);
      } else if (currentUser.role === 'supervisor') {
        // Supervisor sees all appointments for RBTs they supervise
        userAppointments = await appointmentService.getAppointmentsBySupervisor(currentUser.id);
      } else if (currentUser.role === 'admin') {
        // Admin sees all appointments in company
        userAppointments = await appointmentService.getAppointmentsByCompany(company.id);
      }

      // Auto-mark past-due, never-started appointments as no-show.
      const reconciled = await appointmentService.reconcileMissedAppointments(userAppointments);
      setAppointments(reconciled);
      setError(null);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAppointment = async (appointmentData: AppointmentFormData) => {
    if (!company || !currentUser) {
      throw new Error('No company or user selected');
    }

    // Get the selected client
    const client = clients.find((c) => c.id === appointmentData.clientId);
    if (!client) {
      throw new Error('Client not found');
    }

    // Use the RBT explicitly chosen in the form, else the client's assigned RBT.
    const rbtId = appointmentData.rbtId || client.assignedRbtIds?.[0];
    if (!rbtId) {
      throw new Error('This client has no assigned RBT. Please select an RBT.');
    }

    // Get the RBT's supervisor
    const rbtUser = users.find(u => u.id === rbtId);
    if (!rbtUser) {
      throw new Error('RBT user not found');
    }

    // Determine supervisor: use RBT's supervisor, or current user if they're a supervisor/admin
    let supervisorId = rbtUser.supervisorId;
    if (!supervisorId) {
      if (currentUser.role === 'supervisor') {
        supervisorId = currentUser.id;
      } else if (currentUser.role === 'admin') {
        // Admin booking - find any supervisor or use admin as fallback
        const anySupervisor = users.find(u => u.role === 'supervisor');
        supervisorId = anySupervisor?.id || currentUser.id;
      } else {
        throw new Error('No supervisor assigned to the RBT. Please contact your administrator.');
      }
    }

    await appointmentService.createAppointment(
      company.id,
      rbtId,
      supervisorId,
      client,
      currentUser.id,
      appointmentData
    );

    setSuccessMessage('Appointment booked successfully!');
    await loadData();

    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleRescheduleSubmit = async (appointmentData: AppointmentFormData) => {
    if (!editingAppointment) return;
    await appointmentService.updateAppointment(editingAppointment.id, appointmentData);
    setSuccessMessage('Appointment updated!');
    setEditingAppointment(null);
    await loadData();
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const getClientName = (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    return client ? `${client.firstName} ${client.lastName}` : 'Unknown Client';
  };

  const getRbtName = (rbtId: string) => {
    const rbt = users.find((u) => u.id === rbtId);
    return rbt ? rbt.name : 'Unknown RBT';
  };

  const getSupervisorName = (supervisorId: string) => {
    const supervisor = users.find((u) => u.id === supervisorId);
    return supervisor ? supervisor.name : 'Unknown Supervisor';
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'badge-primary';
      case 'checked-in':
        return 'badge-warning';
      case 'in-progress':
        return 'badge-success';
      case 'completed':
        return 'badge-secondary';
      case 'cancelled':
        return 'badge-error';
      case 'no-show':
        return 'badge-error';
      default:
        return '';
    }
  };

  const formatAppointmentDate = (date: Date) => {
    if (isToday(date)) {
      return `Today, ${format(date, 'MMMM d, yyyy')}`;
    } else if (isTomorrow(date)) {
      return `Tomorrow, ${format(date, 'MMMM d, yyyy')}`;
    } else {
      return format(date, 'EEEE, MMMM d, yyyy');
    }
  };

  const getFilteredAppointments = () => {
    const today = startOfDay(new Date());
    return appointments.filter((apt) => {
      // Filter by upcoming (future appointments)
      if (apt.startTime < today) return false;

      // Completed / cancelled appointments aren't "upcoming" (unless the user
      // explicitly filters for that status below).
      if (
        (apt.status === 'completed' || apt.status === 'cancelled') &&
        filterStatus !== apt.status
      ) {
        return false;
      }

      // Filter by RBT
      if (filterRbt && apt.rbtId !== filterRbt) return false;

      // Filter by Client
      if (filterClient && apt.clientId !== filterClient) return false;

      // Filter by Status
      if (filterStatus && apt.status !== filterStatus) return false;

      // Filter by Start Date
      if (filterStartDate) {
        const startDate = new Date(filterStartDate);
        if (apt.startTime < startDate) return false;
      }

      // Filter by End Date
      if (filterEndDate) {
        const endDate = new Date(filterEndDate);
        endDate.setHours(23, 59, 59, 999); // End of day
        if (apt.startTime > endDate) return false;
      }

      return true;
    });
  };

  const clearFilters = () => {
    setFilterRbt('');
    setFilterClient('');
    setFilterStatus('');
    setFilterStartDate('');
    setFilterEndDate('');
  };

  const hasActiveFilters = () => {
    return filterRbt || filterClient || filterStatus || filterStartDate || filterEndDate;
  };

  const upcomingAppointments = getFilteredAppointments();
  const todayAppointments = appointments.filter((apt) => isToday(apt.startTime));

  // Group upcoming appointments by date for display
  const groupedUpcomingAppointments = useMemo(() => {
    const grouped: { [key: string]: Appointment[] } = {};
    upcomingAppointments.forEach((apt) => {
      const dateKey = format(apt.startTime, 'yyyy-MM-dd');
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(apt);
    });
    // Sort each group by time
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    });
    return grouped;
  }, [upcomingAppointments]);

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: 'var(--spacing-8)', textAlign: 'center' }}>
          <div
            style={{
              display: 'inline-block',
              width: '48px',
              height: '48px',
              border: '4px solid var(--color-gray-200)',
              borderTop: '4px solid var(--color-primary)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ marginTop: 'var(--spacing-4)', color: 'var(--color-gray-600)' }}>
            Loading appointments...
          </p>
        </div>
    );
  }

  return (
    <>
      <div className="container" style={{ paddingTop: 'var(--spacing-8)', paddingBottom: 'var(--spacing-8)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-8)' }}>
          <div>
            <h1 style={{ marginBottom: 'var(--spacing-2)' }}>
              {currentUser?.role === 'supervisor' ? 'Supervision Schedule' : currentUser?.role === 'admin' ? 'All Appointments' : 'My Appointments'}
            </h1>
            <p style={{ color: 'var(--color-gray-600)' }}>
              {currentUser?.role === 'supervisor'
                ? `Viewing appointments for all your RBTs`
                : `${upcomingAppointments.length} upcoming appointments`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center' }}>
            {/* View Toggle - only for admin */}
            {currentUser?.role === 'admin' && (
              <div style={{ display: 'flex', border: '1px solid var(--color-gray-300)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                <button
                  onClick={() => setViewMode('list')}
                  style={{
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    background: viewMode === 'list' ? 'var(--color-primary)' : 'white',
                    color: viewMode === 'list' ? 'white' : 'var(--color-gray-700)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-1)',
                    fontSize: 'var(--text-sm)',
                  }}
                  title="List View"
                >
                  <Grid size={16} />
                  List
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  style={{
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    background: viewMode === 'table' ? 'var(--color-primary)' : 'white',
                    color: viewMode === 'table' ? 'white' : 'var(--color-gray-700)',
                    border: 'none',
                    borderLeft: '1px solid var(--color-gray-300)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-1)',
                    fontSize: 'var(--text-sm)',
                  }}
                  title="Table View"
                >
                  <List size={16} />
                  Table
                </button>
              </div>
            )}
            {(currentUser?.role === 'rbt' || currentUser?.role === 'admin') && (
              <button
                onClick={() => setShowAddAppointmentModal(true)}
                className="btn btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}
              >
                <Plus size={20} />
                Book Appointment
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--spacing-6)' }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success" style={{ marginBottom: 'var(--spacing-6)' }}>
            {successMessage}
          </div>
        )}

        {/* Filter Controls */}
        <div className="card" style={{ marginBottom: 'var(--spacing-6)' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 'var(--spacing-4)',
              borderBottom: showFilters ? '1px solid var(--color-gray-200)' : 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
              <Filter size={18} />
              <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)' }}>
                Filters
                {hasActiveFilters() && (
                  <span style={{ marginLeft: 'var(--spacing-2)', fontSize: 'var(--text-sm)', color: 'var(--color-primary)' }}>
                    (Active)
                  </span>
                )}
              </h3>
            </div>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
              {hasActiveFilters() && (
                <button
                  onClick={clearFilters}
                  className="btn btn-secondary"
                  style={{ fontSize: 'var(--text-sm)', padding: 'var(--spacing-2) var(--spacing-3)' }}
                >
                  Clear All
                </button>
              )}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="btn btn-secondary"
                style={{ fontSize: 'var(--text-sm)', padding: 'var(--spacing-2) var(--spacing-3)' }}
              >
                {showFilters ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          {showFilters && (
            <div style={{ padding: 'var(--spacing-4)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--spacing-4)' }}>
                {/* Date Range */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" htmlFor="filterStartDate">
                    Start Date
                  </label>
                  <input
                    id="filterStartDate"
                    type="date"
                    className="form-input"
                    value={filterStartDate}
                    onChange={(e) => setFilterStartDate(e.target.value)}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" htmlFor="filterEndDate">
                    End Date
                  </label>
                  <input
                    id="filterEndDate"
                    type="date"
                    className="form-input"
                    value={filterEndDate}
                    onChange={(e) => setFilterEndDate(e.target.value)}
                  />
                </div>

                {/* RBT Filter - only show for admin/supervisor */}
                {(currentUser?.role === 'admin' || currentUser?.role === 'supervisor') && (
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor="filterRbt">
                      RBT
                    </label>
                    <select
                      id="filterRbt"
                      className="form-input"
                      value={filterRbt}
                      onChange={(e) => setFilterRbt(e.target.value)}
                    >
                      <option value="">All RBTs</option>
                      {users
                        .filter((u) => u.role === 'rbt')
                        .map((rbt) => (
                          <option key={rbt.id} value={rbt.id}>
                            {rbt.name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                {/* Client Filter */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" htmlFor="filterClient">
                    Client
                  </label>
                  <select
                    id="filterClient"
                    className="form-input"
                    value={filterClient}
                    onChange={(e) => setFilterClient(e.target.value)}
                  >
                    <option value="">All Clients</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.firstName} {client.lastName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status Filter */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" htmlFor="filterStatus">
                    Status
                  </label>
                  <select
                    id="filterStatus"
                    className="form-input"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="checked-in">Checked In</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="no-show">No Show</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-3" style={{ marginBottom: 'var(--spacing-8)', gap: 'var(--spacing-6)' }}>
          <div className="card">
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '48px',
                  height: '48px',
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: 'var(--color-secondary)',
                  color: 'var(--color-white)',
                  marginBottom: 'var(--spacing-3)',
                }}
              >
                <Calendar size={24} />
              </div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>Today's Sessions</p>
              <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)' }}>
                {todayAppointments.length}
              </p>
            </div>
          </div>

          <div className="card">
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '48px',
                  height: '48px',
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-white)',
                  marginBottom: 'var(--spacing-3)',
                }}
              >
                <Clock size={24} />
              </div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>Upcoming</p>
              <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)' }}>
                {upcomingAppointments.length}
              </p>
            </div>
          </div>

          <div className="card">
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '48px',
                  height: '48px',
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: 'var(--color-success)',
                  color: 'var(--color-white)',
                  marginBottom: 'var(--spacing-3)',
                }}
              >
                <Video size={24} />
              </div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>Camera Required</p>
              <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)' }}>
                {upcomingAppointments.filter((a) => a.cameraRequired).length}
              </p>
            </div>
          </div>
        </div>

        {/* Appointments List */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Upcoming Appointments</h2>
          </div>

          {upcomingAppointments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-8)', color: 'var(--color-gray-500)' }}>
              <Calendar size={48} style={{ margin: '0 auto var(--spacing-4)' }} />
              <p>No upcoming appointments</p>
              {currentUser?.role === 'rbt' && (
                <p style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--spacing-2)' }}>
                  Click "Book Appointment" to schedule a session
                </p>
              )}
            </div>
          ) : viewMode === 'table' && currentUser?.role === 'admin' ? (
            /* Table View - Admin Only */
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Client</th>
                    <th>RBT</th>
                    <th>Supervisor</th>
                    <th>Location</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th>Camera</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingAppointments.map((appointment) => (
                    <tr key={appointment.id}>
                      <td style={{ fontWeight: 'var(--font-medium)' }}>
                        {format(appointment.startTime, 'MMM d, yyyy')}
                      </td>
                      <td>{format(appointment.startTime, 'h:mm a')}</td>
                      <td>{getClientName(appointment.clientId)}</td>
                      <td>{getRbtName(appointment.rbtId)}</td>
                      <td>{getSupervisorName(appointment.supervisorId)}</td>
                      <td>
                        {appointment.location.city}, {appointment.location.state}
                      </td>
                      <td>{appointment.duration} min</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(appointment.status)}`}>
                          {appointment.status.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        </span>
                      </td>
                      <td>
                        {appointment.cameraRequired ? (
                          <span className="badge badge-success" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)' }}>
                            <Video size={12} />
                            Yes
                          </span>
                        ) : (
                          <span style={{ color: 'var(--color-gray-400)' }}>—</span>
                        )}
                      </td>
                      <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {appointment.notes || <span style={{ color: 'var(--color-gray-400)' }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-6)' }}>
              {Object.keys(groupedUpcomingAppointments)
                .sort()
                .map((dateKey) => (
                  <div key={dateKey}>
                    <h3
                      style={{
                        fontSize: 'var(--text-lg)',
                        fontWeight: 'var(--font-semibold)',
                        color: 'var(--color-gray-700)',
                        marginBottom: 'var(--spacing-4)',
                        paddingBottom: 'var(--spacing-2)',
                        borderBottom: '2px solid var(--color-primary)',
                      }}
                    >
                      {formatAppointmentDate(groupedUpcomingAppointments[dateKey][0].startTime)}
                    </h3>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
                      {groupedUpcomingAppointments[dateKey].map((appointment) => (
                        <div
                          key={appointment.id}
                          style={{
                            padding: 'var(--spacing-4)',
                            border: '1px solid var(--color-gray-200)',
                            borderRadius: 'var(--radius-lg)',
                            backgroundColor: isToday(appointment.startTime)
                              ? 'var(--color-light-accent)'
                              : 'var(--color-gray-50)',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              marginBottom: 'var(--spacing-3)',
                            }}
                          >
                            <div>
                              <h4 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-1)' }}>
                                {getClientName(appointment.clientId)}
                              </h4>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
                                <span className={`badge ${getStatusBadgeClass(appointment.status)}`}>
                                  {appointment.status.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                                </span>
                                {appointment.cameraRequired && (
                                  <span className="badge badge-success" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)' }}>
                                    <Video size={12} />
                                    Camera
                                  </span>
                                )}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <p style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', color: 'var(--color-primary)' }}>
                                {format(appointment.startTime, 'h:mm a')}
                              </p>
                              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
                                {appointment.duration} min
                              </p>
                            </div>
                          </div>

                          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
                            {(currentUser?.role === 'supervisor' || currentUser?.role === 'admin') && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
                                <UserIcon size={14} />
                                <span>RBT: {getRbtName(appointment.rbtId)}</span>
                              </div>
                            )}
                            {currentUser?.role === 'admin' && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
                                <UserIcon size={14} />
                                <span>Supervisor: {getSupervisorName(appointment.supervisorId)}</span>
                              </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-2)' }}>
                              <MapPin size={14} style={{ marginTop: '2px', flexShrink: 0 }} />
                              <span>
                                {appointment.location.address}, {appointment.location.city}, {appointment.location.state}{' '}
                                {appointment.location.zipCode}
                              </span>
                            </div>
                          </div>

                          {appointment.notes && (
                            <div
                              style={{
                                marginTop: 'var(--spacing-3)',
                                padding: 'var(--spacing-3)',
                                backgroundColor: 'var(--color-gray-100)',
                                borderLeft: '3px solid var(--color-primary)',
                                borderRadius: 'var(--radius-md)',
                              }}
                            >
                              <p style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--color-gray-700)', marginBottom: 'var(--spacing-1)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Session Notes
                              </p>
                              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-700)' }}>
                                {appointment.notes}
                              </p>
                            </div>
                          )}

                          {(currentUser?.role === 'supervisor' || currentUser?.role === 'admin') &&
                            appointment.status !== 'completed' &&
                            appointment.status !== 'cancelled' && (
                              <div style={{ marginTop: 'var(--spacing-3)', display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                  className="btn btn-secondary"
                                  style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}
                                  onClick={() => setEditingAppointment(appointment)}
                                >
                                  <Clock size={14} />
                                  Reschedule
                                </button>
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <AddAppointmentModal
        isOpen={showAddAppointmentModal}
        onClose={() => setShowAddAppointmentModal(false)}
        onSubmit={handleAddAppointment}
        clients={clients.filter((c) => c.status === 'active')}
        rbts={users.filter((u) => u.role === 'rbt')}
      />

      <AddAppointmentModal
        isOpen={!!editingAppointment}
        onClose={() => setEditingAppointment(null)}
        onSubmit={handleRescheduleSubmit}
        clients={clients}
        rbts={users.filter((u) => u.role === 'rbt')}
        initialAppointment={editingAppointment || undefined}
      />
    </>
  );
};
